import * as React from "react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import type { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * Auth Context Types
 */
interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
}

interface AuthContextType extends AuthState {
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication state and methods.
 * Uses Supabase Auth for session management.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
  });

  // Initialize auth state and listen for changes
  useEffect(() => {
    // Detect invite token in URL hash (from Supabase dashboard invite)
    const hash = window.location.hash;
    const isInvite = hash.includes("type=invite");

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
        error: error ?? null,
      }));

      // If this is an invite flow and we have a session, redirect to set password
      if (isInvite && session) {
        window.location.href = "/reset-password";
      }
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }));

      // Handle invite flow: when Supabase processes the invite token,
      // it fires a SIGNED_IN event. Redirect to password setup.
      if (isInvite && event === "SIGNED_IN" && session) {
        window.location.href = "/reset-password";
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(
    async (email: string, password: string, rememberMe = true) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setState((prev) => ({ ...prev, loading: false, error }));
        throw error;
      }

      // Note: Session persistence is handled by Supabase client config
      // The rememberMe flag could be used to set shorter session duration
      // but Supabase doesn't support per-signin session config
      // Instead we rely on the default persistSession: true in supabase client
    },
    []
  );

  /**
   * Sign up with email and password
   */
  const signUp = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Redirect to app after email confirmation
        emailRedirectTo: `${window.location.origin}/`,
      },
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    // Send welcome email (fire-and-forget, don't block signup)
    fetch("/api/email/welcome", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }).catch((err) => {
      console.error("Failed to send welcome email:", err);
    });

    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  /**
   * Sign in with Google OAuth
   */
  const signInWithGoogle = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }
  }, []);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.signOut({ scope: "local" });

    // If sign-out fails because there's no session, that's fine — clear local state anyway
    if (error && error.name !== "AuthSessionMissingError") {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    setState((prev) => ({
      ...prev,
      user: null,
      session: null,
      loading: false,
    }));
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  /**
   * Update password (after reset or from profile)
   */
  const updatePassword = useCallback(async (newPassword: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
      throw error;
    }

    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextType = {
    ...state,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

export { AuthContext };
export type { AuthContextType, AuthState };
